/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FIPApiService, IpGroupApiService, Layer4ApiService } from '../../../../http';
import { FipModel, IpGroupModel, L4FrontendServiceModel } from '../../../../models';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { LbErrorService, PortsService, WizardService, WizardTemplateService } from '../../../../services';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin } from 'rxjs';
import { nameValidation, portRangeValidation } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-layer4-frontend',
    templateUrl: './layer4-frontend.component.html',
    styleUrls: ['./layer4-frontend.component.scss']
})

export class Layer4FrontendComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        balanceModes: []
    };

    /**
     * Store the port validation errors, so they are accessible from the view.
     */
    public portValidations: ErrorResponseModel;

    /**
     * Store the floatingIp validation error, so it is accessible from the view.
     */
    public fipValidation: ErrorResponseModel;

    /**
     * Used to pass value to floating IP component.
     */
    public floatingIPs: FipModel[];

    /**
     * Store the FIP groups.
     */
    public ipGroups: IpGroupModel[];

    /**
     * A boolean to determine if the form should be full width or not.
     */
    public isFormFullWidth = false;

    /**
     * Store frontends for name validation.
     * @private
     */
    private frontends: L4FrontendServiceModel[] = [];

    /**
     * Store individual ports.
     * @private
     */
    private _portIndividual: FormArray;

    /**
     * List of acl rules which is part of the form.
     * @private
     */
    private _portRange: FormArray;

    /**
     * Helper for validation when both range and individual ports are toggled off.
     * @private
     */
    private portsValid = true;

    constructor(
        private fipApiService: FIPApiService,
        private ipGroupApiService: IpGroupApiService,
        private layer4ApiService: Layer4ApiService,
        private portsService: PortsService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
    }

    ngOnInit() {
        this.componentTitle = WizardTitleEnum.FRONTENDS;
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Clears a formArray's validation.
     * @param fields
     * @param childField
     * @private
     */
    private static clearFieldValidation(fields: any, childField?: string) {
        for (const field of fields) {
            const input = childField ? field.controls[childField] : field;
            if (input.hasError('responseError')) {
                input.markAsUntouched();
                input.setErrors({ 'responseError': null });
                input.updateValueAndValidity();
            }
        }
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get portIndividual(): FormArray {
        this._portIndividual = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals') as FormArray;

        return this._portIndividual;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get portRange(): FormArray {
        this._portRange = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges') as FormArray;

        return this._portRange;
    }

    /**
     * Sets the frontend form value for Fip ID.
     * @param event
     */
    public setFipId(event: any): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.FLOATING_IPS + '.fipId').setValue(event);
    }

    /**
     * Add subject alt row in the form.
     */
    public addPortIndividual(): void {
        const previousValue: any = this.portIndividual.controls[this.portIndividual.controls.length - 1].value;
        const newValue: number = previousValue === 65535 ? 0 : parseInt(previousValue, 10) + 1;
        this.portIndividual.push(new FormControl(newValue, [Validators.required, portRangeValidation]));
        // Check for errors after adding
        const addPort = window.setTimeout(() => {
            const formIndividuals = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').value;
            this.portIndividualHasErrors(formIndividuals);
            window.clearTimeout(addPort);
        }, 10);
    }

    /**
     * Delete subject alt row from the form.
     */
    public removePortIndividual(i: number): void {
        this.portIndividual.removeAt(i);
        this.checkForDuplicatePorts();
    }

    /**
     * Add port range row in the form.
     */
    public addPortRange(): void {
        this._portRange = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges') as FormArray;
        this._portRange.push(this.createRangeFormControlRows());
    }

    /**
     * Delete port range row in the form.
     */
    public removePortRange(index: number): void {
        // Remove the old line
        this._portRange.removeAt(index);
    }

    /**
     * Helper function to display the correct min value in the port end range.
     */
    public getStartMinValue(index: number): any {
        if (index > 0) {
            const lastRangeEnd: any = this.portRange.controls[index - 1]['controls']['portEnd']?.value;
            const parsedMinValue: number = lastRangeEnd === 65535 ? 0 : parseInt(lastRangeEnd, 10) + 1;
            if (isNaN(parsedMinValue)) {
                return null;
            } else {
                return parsedMinValue;
            }
        }
    }

    /**
     * Helper function to display the correct min value in the port end range.
     */
    public getEndMinValue(index: number): any {
        const startValue: any = this.portRange.controls[index]['controls']['portStart'].value;
        const parsedMinValue: number = parseInt(startValue, 10) + 1;
        if (isNaN(parsedMinValue)) {
            return null;
        } else {
            return parsedMinValue;
        }
    }

    /**
     * Check for duplicate port values.
     */
    public checkForDuplicatePorts(): void {
        const duplicates: any[] = [];

        for (const control of this.portIndividual.controls) {
            if (duplicates.includes(control.value)) {
                control.setErrors({ 'duplicateValidator': `${this.languageService.translate('duplicate_port')}` });
            } else {
                if (control.hasError('duplicateValidator')) {
                    control.setErrors({ 'duplicateValidator': null });
                    control.updateValueAndValidity();
                }
            }
            duplicates.push(control.value);
        }
    }

    /**
     * Check all the ranges to see any overlap and adds errors if needed.
     */
    public checkForOverlappingRange(): void {
        const ranges: any[] = [];
        for (const control of this.portRange.controls) {
            const formControlStart = control.get('portStart');
            if (this.isOverlap(control.value.portStart, control.value.portEnd, ranges)) {
                formControlStart.setErrors({ 'rangeOverlap': `${this.languageService.translate('port_range_overlap')}` });
            } else {
                if (formControlStart.hasError('rangeOverlap')) {
                    formControlStart.setErrors({ 'rangeOverlap': null });
                    formControlStart.updateValueAndValidity();
                }
            }
            ranges.push(control.value);
        }
    }

    /**
     * Creates a form controller for each path in the template in edit mode.
     * @param total
     * @private
     */
    private createIndividualPortFormControls(total: number): void {
        for (let i = 0; i < total; i++) {
            this.portIndividual.push(new FormControl(null, Validators.required));
        }
    }

    /**
     * Creates a form controller for each range of ports in the template in edit mode.
     * @param total
     * @private
     */
    private createRangePortFormControls(total: number): void {
        for (let i = 0; i < total; i++) {
            this.addPortRange();
        }
    }

    /**
     * Create additional port range in the form on the fly.
     * @private
     */
    private createRangeFormControlRows(): FormGroup {
        const index: number = this._portRange?.length;
        const startValue = this.getStartMinValue(index) ? this.getStartMinValue(index) : 80;
        const disabled = index === undefined;

        return this.formBuilder.group({
            portStart: [{ value: startValue, disabled: disabled }, [Validators.required, portRangeValidation]],
            portEnd: [{ value: null, disabled: disabled }, [Validators.required, portRangeValidation]],
        });
    }

    /**
     * Set the minimum value of the range to the last individual value + 1.
     * @private
     */
    private setMinValue(): void {
        const portIndividualValues: any[] = this.portIndividual.value;
        const lastValue: any = portIndividualValues[portIndividualValues.length - 1];
        const portRangeValues: any[] = this.portRange.value;
        if (portRangeValues.length === 1 && portRangeValues[0].portEnd === null) {
            this.portRange.controls[0]['controls']['portStart'].setValue(lastValue + 1);
        }
    }

    /**
     * Toggle port enabled validation.
     * @private
     */
    private portEnableValidation(): void {
        const enableIndividual = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableIndividuals');
        const enableRanges = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableRanges');
        // If both checkboxes are unchecked, then portsValid is false
        this.portsValid = !(!enableIndividual.value && !enableRanges.value);
        if (!this.portsValid) {
            enableRanges.markAsTouched();
            enableIndividual.markAsTouched();
            const portUncheckedError = this.languageService.translate('port_none_checked_error');
            enableRanges.setErrors({ 'atLeastOne': `${portUncheckedError}` });
            enableIndividual.setErrors({ 'atLeastOne': `${portUncheckedError}` });
        } else {
            if (enableIndividual.hasError('atLeastOne')) {
                enableIndividual.setErrors({ 'atLeastOne': null });
                enableIndividual.updateValueAndValidity();
            }

            if (enableRanges.hasError('atLeastOne')) {
                enableRanges.setErrors({ 'atLeastOne': null });
                enableRanges.updateValueAndValidity();
            }
        }
    }

    /**
     * Toggle port ranges.
     * @param enableRanges
     * @private
     */
    private togglePortRanges(enableRanges: boolean): void {
        if (enableRanges) {
            if (this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableIndividuals').value) {
                this.setMinValue();
            }
        }

        for (const range of this.portRange.controls) {
            if (enableRanges) {
                range.enable();
            } else {
                range.disable();
            }
        }
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setupFormGroup(): void {
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                // General
                id: [''], // <- Used when editing.
                aclIds: [[]], // <- Used to store the default ACL ID on first build
                name: [null, [Validators.required, nameValidation]],
                mode: ['tcpudp', null],
                enableIndividuals: [true],
                portIndividuals: new FormArray([
                    new FormControl(80, [Validators.required, portRangeValidation])
                ]),
                enableRanges: [false],
                ports: [null],
                portRanges: this.formBuilder.array([this.createRangeFormControlRows()])
            }),
            [WizardSubStepTitlesEnum.FLOATING_IPS]: this.formBuilder.group({
                // Floating IP
                fipId: [null, Validators.required],
                fipEnabled: [true]
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.FRONTENDS);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    // Validate the form each step of the way. Use debounce to reduce the number of times the callback is called.
                    this.utilsService.debounce('frontends', () => {
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    });
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(name => {
                this.checkNameForDuplicates(name, this.frontends);
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableIndividuals').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(enableIndividuals => {
                this.portEnableValidation();
                if (enableIndividuals) {
                    this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').enable();
                } else {
                    this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').disable();
                }
                // Check for errors after toggle
                const formIndividuals = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').value;
                this.portIndividualHasErrors(formIndividuals);

            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(formIndividuals => {
                this.portIndividualHasErrors(formIndividuals);
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableRanges').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(enableRanges => {
                this.portEnableValidation();
                this.togglePortRanges(enableRanges);
                // Check for errors after toggle
                const formRanges = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges').value;
                this.portRangeHasErrors(formRanges);
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(formRanges => {
                this.portRangeHasErrors(formRanges);
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.FLOATING_IPS + '.fipId').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(fipId => {
                if (fipId === '$1') {
                    this.serviceForm.get(WizardSubStepTitlesEnum.FLOATING_IPS + '.fipId').setValue(null);
                }
            });
    }

    /**
     * Setup wizard subscribers.
     * @private
     */
    private setupWizardSubscribers(): void {
        /**
         * Advanced Mode Subscriber
         * Note: We subscribe to whether the advanced mode button has been toggled.
         */
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                advancedState => {
                    this.advancedMode = advancedState;
                }
            );

        this.wizardService.errorNotificationClosed
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                state => {
                    if (state) {
                        // Clear validation variables
                        this.portValidations = null;
                        this.fipValidation = null;
                        this.clearPortErrorValidation();
                        this.clearFieldValidation();
                    }
                }
            );

        this.wizardService.subStepNameEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(step => {
                this.isFormFullWidth = step === WizardSubStepTitlesEnum.FLOATING_IPS;
            });
    }

    /**
     * Loads both the floating IP groups and Interfaces and set the values.
     * @private
     */
    private initLoading(): void {
        const frontendId = this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${[WizardTitleEnum.FRONTENDS]}.${this.templateIndex}.id.value`
        );
        const apiCalls = this.getApiCalls(frontendId);
        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (dataGroup: object) => {
                    const serviceError = this.getErrorForService(WizardTitleEnum.FRONTENDS);
                    const template = this.getTemplate(WizardTitleEnum.FRONTENDS, serviceError);

                    // Don't allow 'add another' if editing.
                    if (this.utilsService.isServiceId(template.id?.value)) {
                        this.wizardService.excludeFromAddAnother = WizardTitleEnum.FRONTENDS;
                    }

                    this.ipGroups = this.formatIpGroups(dataGroup['ipGroups']);
                    this.floatingIPs = this.formatFloatingIPs(dataGroup['floatingIPs']);

                    const services: L4FrontendServiceModel[] = this.formatServices(dataGroup['frontends'], WizardTitleEnum.FRONTENDS);
                    this.frontends = template.id ? this.filterServicesByName(template, services) : services;

                    this.patchTemplateToForm(template);
                    // Once everything is loaded check to see if there are any error in the controller state
                    if (serviceError?.error?.length) {
                        this.setErrorStateOnPage(serviceError.error);
                    }
                }
            );
    }

    /**
     * Get api calls.
     * @param frontendId
     * @private
     */
    private getApiCalls(frontendId?: string): any {
        const apiCalls = {
            ipGroups: this.ipGroupApiService.getGroups(),
            floatingIPs: this.fipApiService.getFloatingIPs(),
            frontends: this.layer4ApiService.getFrontends()
        };

        if (this.utilsService.isServiceId(frontendId)) {
            apiCalls['backends'] = this.layer4ApiService.getBackendsByFrontendId(frontendId);
        }

        return apiCalls;
    }

    /**
     * Initialises the form values.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdownContent();

        // Populate our patch
        const templatePatch = this.createTemplatePatch(template);

        // Format name and prefix
        this.formatLabel(templatePatch, template.name.value);

        // Set up the ports based on template values.
        if (templatePatch['ports']) {
            this.setUpPorts(templatePatch);
        }

        // Check if we are in advanced mode and set the correct states on the inputs before patching
        this.advancedMode = this.wizardService.wizardMode;

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.FRONTENDS);

        // If a property is set as null in the template remove the property so the default if used.
        this.removeNullValueProperties(templatePatch);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        // Check the name after patching
        this.checkNameForDuplicates(template.name.value, this.frontends, 'name', true);

        this.isFormFullWidth = this.currentSubStepName === WizardSubStepTitlesEnum.FLOATING_IPS;

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;

        // Once patched output the values to the controller state.
        this.outputFormValues();
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown.balanceModes = [
            <DropdownModel>{
                key: 'tcp',
                value: 'TCP'
            },
            <DropdownModel>{
                key: 'udp',
                value: 'UDP'
            },
            <DropdownModel>{
                key: 'tcpudp',
                value: 'TCP + UDP'
            },
            <DropdownModel>{
                key: 'ops',
                value: 'One Packet Scheduling'
            }
        ];
    }

    /**
     * Sets the view and form values for ports.
     * @param templatePatch
     * @private
     */
    private setUpPorts(templatePatch: any): void {
        const ports = templatePatch['ports'];
        const individuals = [];
        const ranges = [];

        for (const port of ports) {
            if (port.length > 1) {
                ranges.push({
                    portStart: port[0],
                    portEnd: port[1]
                });
            } else {
                individuals.push(port);
            }
        }

        if (individuals.length) {
            const portIndividuals = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals') as FormArray;
            const totalToCreate = individuals.length - (portIndividuals && portIndividuals.controls.length);
            this.createIndividualPortFormControls(totalToCreate);
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableIndividuals').setValue(true);
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').setValue(individuals);
        } else {
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableIndividuals').setValue(false);
        }

        if (ranges.length) {
            const portRanges = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges') as FormArray;
            const totalToCreate = ranges.length - (portRanges && portRanges.controls.length);
            this.createRangePortFormControls(totalToCreate);
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableRanges').setValue(true);
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges').setValue(ranges);
        } else {
            this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.enableRanges').setValue(false);
        }
    }

    /**
     * Check for error state.
     * @param frontendError
     * @private
     */
    private setErrorStateOnPage(frontendError: any[]): void {
        this.loadedWithErrors(frontendError);
        this.getPortValidationFromErrorObject(frontendError);
        this.fipValidation = this.lbErrorService.getFloatingIpValidationFromErrorObject(frontendError);
    }

    /**
     * Checks error response for port errors and set class variable.
     * @param frontendError
     */
    private getPortValidationFromErrorObject(frontendError: any[]): void {
        const errorHasPorts = frontendError.filter(error => error.field === 'ports');
        if (errorHasPorts.length) {
            this.portValidations = errorHasPorts[0];
            this.portValidations.formattedData = this.createValuesFromMessage();
            // Set initial errors
            const formIndividuals = this.utilsService.getObjectValue(this.portValidations, 'formattedData.portIndividuals');
            this.portIndividualHasErrors(formIndividuals);

            const formRanges = this.utilsService.getObjectValue(this.portValidations, 'formattedData.portRanges');
            this.portRangeHasErrors(formRanges);
        }
    }

    /**
     * Create a ports values object the same as the form values object.
     * @private
     */
    private createValuesFromMessage(): any {
        const portValues = this.portValidations.value.split(',');
        const portIndividuals = [];
        const portRanges = [];
        for (const value of portValues) {
            const ranges = value.split('-');
            if (ranges.length > 1) {
                portRanges.push({
                    portStart: parseInt(ranges[0], 10),
                    portEnd: parseInt(ranges[1], 10)
                });
            } else {
                portIndividuals.push(parseInt(value, 10));
            }
        }

        return { portIndividuals, portRanges };
    }

    /**
     * Takes form values and loops through port validation object.
     * @param formIndividuals
     * @private
     */
    private portIndividualHasErrors(formIndividuals: any[]): void {
        // Check for ranges
        if (formIndividuals?.length) {
            let index = 0;

            // Loop through each set of ranges in the form
            for (const formIndividual of formIndividuals) {
                const portIndividual = this.serviceForm.get(`${WizardSubStepTitlesEnum.GENERAL}.portIndividuals.${index}`);
                if (this.setValidationOnErrorFields(portIndividual, formIndividual)) {
                    break;
                }
                index++;
            }
        }
        this.portsService.formatPortsForOutput(this.serviceForm);
    }

    /**
     * Takes form values and loops through port validation object.
     * @param formRanges
     * @private
     */
    private portRangeHasErrors(formRanges: any[]): void {
        // Check for ranges
        if (formRanges?.length) {
            let index = 0;

            // Loop through each set of ranges in the form
            for (const formRange of formRanges) {
                const portStart = this.serviceForm.get(`${WizardSubStepTitlesEnum.GENERAL}.portRanges.${index}.portStart`);
                if (this.setValidationOnErrorFields(portStart, formRange)) {
                    // Break after the first error, so we only show one error at a time.
                    break;
                }
                index++;
            }
        }
        this.portsService.formatPortsForOutput(this.serviceForm);
    }

    /**
     * Checks if field is validation and adds/removes validation.
     * @param field
     * @param formValue
     * @private
     */
    private setValidationOnErrorFields(field: any, formValue: any): boolean {
        if (formValue) {
            const hasErrors = formValue.portStart
                ? this.isInErrorRange(formValue)
                : this.isErrorIndividual(formValue);
            if (hasErrors) {
                field.markAsTouched();
                field.setErrors({ 'responseError': this.portValidations.message });

                return true;
            } else {
                if (field.hasError('responseError')) {
                    field.markAsUntouched();
                    field.setErrors({ 'responseError': null });
                    field.updateValueAndValidity();
                }
            }
        }
    }

    /**
     * Checks individual ports for errors.
     * @param formValue
     * @private
     */
    private isErrorIndividual(formValue: string): boolean {
        const errors: any[] = [];
        if (this.portValidations && this.portValidations.formattedData.portIndividuals) {
            for (const erroredIndividual of this.portValidations.formattedData.portIndividuals) {
                const formattedFormValue = parseInt(formValue, 10);
                if (erroredIndividual === formattedFormValue) {
                    errors.push(true);
                }
            }

            return !!errors.length;
        }
    }

    /**
     * Checks port ranges for errors.
     * @param formRange
     * @private
     */
    private isInErrorRange(formRange: any): boolean {
        const errors = [];
        if (this.portValidations && this.portValidations.formattedData.portRanges) {
            const formStart = parseInt(formRange.portStart, 10);
            const formEnd = parseInt(formRange.portEnd, 10);
            if (this.isOverlap(formStart, formEnd, this.portValidations.formattedData.portRanges)) {
                errors.push(true);
            }

            return !!errors.length;
        }
    }

    /**
     * Check if the start and end ports overlap any ports in the range.
     * @param formStart
     * @param formEnd
     * @param ranges
     * @private
     */
    private isOverlap(formStart: number, formEnd: number, ranges: any[]): boolean {
        return ranges.find(range => (
                (formStart >= range.portStart && formStart <= range.portEnd)
                || (formEnd >= range.portStart && formEnd <= range.portEnd))
            || (formStart <= range.portStart && formEnd >= range.portEnd)
        );
    }

    /**
     * Clears all port validation errors.
     * @private
     */
    private clearPortErrorValidation(): void {
        const portIndividuals = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals') as FormArray;
        Layer4FrontendComponent.clearFieldValidation(portIndividuals.controls);

        const portRanges = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.portRanges') as FormArray;
        Layer4FrontendComponent.clearFieldValidation(portRanges.controls, 'portStart');
    }

    /**
     * Clears the FIP validation.
     * @private
     */
    private clearFieldValidation(): void {
        this.serviceForm.get(this.subStepEnum.FLOATING_IPS + '.fipId').updateValueAndValidity();
    }

    /**
     * Format form values for output.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            // Run this again in case the defaults have been used and nothing has triggered it earlier.
            this.portsService.formatPortsForOutput(this.serviceForm);
            const frontOj: L4FrontendServiceModel = this.flattenServiceFormValues(<L4FrontendServiceModel>{});
            frontOj.ports = this.portsService.updatePortFormat(frontOj.ports);
            frontOj.name = this.formatNameForOutPut();

            const propertiesToDelete = [
                'enableIndividuals',
                'portIndividuals',
                'enableRanges',
                'portRanges'
            ];
            this.removeUnneededProperties(frontOj, propertiesToDelete);

            // loop through form values
            const template = this.wizardService.controllerState[WizardTitleEnum.FRONTENDS][this.templateIndex];
            for (const property in frontOj) {
                if (frontOj.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property]?.hidden;
                    template[property] = {
                        value: frontOj[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }

    /**
     * Format name using prefix
     * @private
     */
    private formatNameForOutPut(): string {
        const formLabel = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').value;
        if (this.namePrefix && formLabel) {
            return this.namePrefix + formLabel;
        }

        return formLabel;
    }
}
